import { Alert, Button, Grid, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { BACKEND_URL } from '../constants';
import { Header, HeaderActionType } from '../Components/Header';


enum PageMode {
  LOGIN,
  SIGNUP
}

export const Login = () => {
  const [username, setUserName] = useState<string | undefined>();
  const [password, setPassword] = useState<string | undefined>();
  const [confirmPassword, setConfirmPassword] = useState<string | undefined>();
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageMode, setPageMode] = useState<PageMode>(PageMode.LOGIN);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const navigate = useNavigate();


  const handleSubmit = async (path: string, onSuccess: () => void) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        // Get the raw text returned by the server
        const serverMessage = await response.text();
        throw new Error(serverMessage || `Login failed (${response.status})`);
      }

      const token = await response.text();
      localStorage.setItem("jwt", token);
      onSuccess();
    } catch (err) {
      setSuccessMessage('');
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidPassword = useMemo(() => {
    if (!password) return true;
    const regex = /^(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    return regex.test(password);
  }, [password]);

  const handlePageModeChange = (mode: PageMode) => {
    setPassword(undefined);
    setConfirmPassword(undefined);
    setUserName(undefined);
    setPageMode(mode);
  }

  if (pageMode === PageMode.SIGNUP) {
    return (
      <Grid
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <Header
          drawerOpen={false}
          activeActions={[HeaderActionType.WORKSPACE]}
        />
        <Grid
          container
          spacing={4}
          direction="column"
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: '100vh' }}
        >
          {/* Form wrapper */}
          <Grid size={4}> {/* size controls width; smaller number = narrower form */}
            <Grid
              container
              spacing={2}
              direction="column"
              component="form"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!username || !password || password !== confirmPassword || !isValidPassword) {
                  return;
                }

                await handleSubmit("/auth/signup", () => {
                  handlePageModeChange(PageMode.LOGIN);
                  setSuccessMessage('Your request has been sent, once it is approved you\'ll be able to login!');
                });
              }}
            >
              <Grid size="auto">
                <Typography
                  color="text.primary"
                  variant="h4"
                  align="center">Sign Up</Typography>
              </Grid>

              {error && (
                <Grid size="auto">
                  <Alert color="error">{error.message}</Alert>
                </Grid>
              )}

              <Grid size="auto">
                <TextField
                  fullWidth
                  label="Username"
                  value={username ?? ''}
                  onChange={(e) => setUserName(e.target.value)}
                  error={username ? username.length < 5 : false}
                  helperText={username && username.length < 5 ? "Username must be at least 5 characters long" : ""}
                />
              </Grid>

              <Grid size="auto">
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password ?? ''}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!isValidPassword}
                  helperText={!isValidPassword ? "Password must be 8 characters long, include 1 number and 1 symbol" : ""}
                />
              </Grid>

              <Grid size="auto">
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword ?? ''}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={password !== confirmPassword}
                  helperText={password !== confirmPassword ? "Must match password above" : ""}
                />
              </Grid>

              <Grid size="auto">
                <Button
                  fullWidth
                  loading={isLoading}
                  variant="contained"
                  type='submit'
                  disabled={!username || !password || !(password === confirmPassword) || !isValidPassword}
                >
                  Signup
                </Button>
              </Grid>
              <Grid size="auto">
                <Button
                  variant='text'
                  fullWidth
                  onClick={() => {
                    handlePageModeChange(PageMode.LOGIN);
                    setError(undefined);
                  }}
                >
                  Already signed up?
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Header
        drawerOpen={false}
        activeActions={[HeaderActionType.WORKSPACE]}
      />
      <Grid
        container
        spacing={4}
        direction="column"
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: '100vh' }}
      >
        {/* Form wrapper */}
        <Grid size={4}> {/* size controls width; smaller number = narrower form */}
          <Grid container spacing={2} direction="column" component="form" onSubmit={async (e) => {
            e.preventDefault();

            if (!username || !password) {
              return;
            }

            await handleSubmit("/auth/login", () => {
              navigate("/dashboard");
            });
          }}>
            <Grid size="auto">
              <Typography
                color="text.primary"
                variant="h4"
                align="center">Login</Typography>
            </Grid>

            {error && (
              <Grid size="auto">
                <Alert severity="error">{error.message}</Alert>
              </Grid>
            )}

            {successMessage && (
              <Grid size="auto">
                <Alert>{successMessage}</Alert>
              </Grid>
            )}

            <Grid size="auto">
              <TextField
                fullWidth
                label="Username"
                value={username ?? ''}
                onChange={(e) => setUserName(e.target.value)}
              />
            </Grid>

            <Grid size="auto">
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password ?? ''}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Grid>

            <Grid size="auto">
              <Button
                fullWidth
                loading={isLoading}
                type="submit"
                variant="contained"
                disabled={!username || !password}
              >
                Login
              </Button>
            </Grid>
            <Grid size="auto">
              <Button
                variant='text'
                fullWidth
                onClick={() => {
                  handlePageModeChange(PageMode.SIGNUP);
                  setError(undefined);
                }}
              >
                Signup
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
